import React, { useEffect, useState, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import backendAPI from "../utils/backendAPI";
import { logout } from "../actions/userActions";
import { ToastContext } from "../App";
import "../css/HomePage.css";
import ProfileDropdown from "./ProfileDropdown";
import MerchantSidebarMenu from "./MerchantSidebarMenu";
import PostComposer from "./PostComposer";

const LoyaltySettingsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const showToast = useContext(ToastContext);
    const { t } = useTranslation();
    const { userInfo } = useSelector((state) => state.userLogin)

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [businessName, setBusinessName] = useState(localStorage.getItem("business_name") || "Store");
    const [adminMerchants, setAdminMerchants] = useState([]);
    const [adminSelectedMerchantId, setAdminSelectedMerchantId] = useState("");

    const [rewardId, setRewardId] = useState(null); // ID of the existing reward to PATCH
    const [rewardType, setRewardType] = useState("");
    const [visits, setVisits] = useState(6);

    const logoutHandler = () => {
        showToast?.("You have been logged out successfully.", "info");
        dispatch(logout());
        navigate("/login", { replace: true });
    };

    const adminMerchantQuery = () =>
        userInfo?.is_admin && adminSelectedMerchantId
            ? { merchant_id: adminSelectedMerchantId }
            : {};

    useEffect(() => {
        if (!userInfo?.is_admin) {
            setAdminMerchants([]);
            setAdminSelectedMerchantId("");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await backendAPI.get("/api/square/merchants/all/");
                if (cancelled) return;
                const list = res.data?.merchants || [];
                setAdminMerchants(list);
                if (list.length === 1) {
                    setAdminSelectedMerchantId(String(list[0].merchant_id || list[0].id || ""));
                }
            } catch {
                if (!cancelled) setAdminMerchants([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userInfo?.is_admin]);

    useEffect(() => {
        let cancelled = false;

        const loadSettings = async () => {
            try {
                if (userInfo?.is_admin && !adminSelectedMerchantId) {
                    if (!cancelled) {
                        setBusinessName(userInfo?.email || "Admin");
                        setRewardId(null);
                        setRewardType("");
                        setVisits(6);
                    }
                    return;
                }

                const res = await backendAPI.get("/api/dashboard/loyalty-settings/", {
                    params: adminMerchantQuery(),
                });
                if (cancelled) return;

                const data = res.data;
                const resolvedName =
                    (data?.business_name || "").trim() ||
                    (userInfo?.is_admin ? userInfo?.email || "Admin" : localStorage.getItem("business_name") || "Store");
                setBusinessName(resolvedName);
                if (resolvedName && resolvedName !== "Store") {
                    localStorage.setItem("business_name", resolvedName);
                }

                const list = Array.isArray(data?.rewards) ? data.rewards : [];
                if (list.length > 0) {
                    const first = list[0];
                    setRewardId(first.reward_id || null);
                    setRewardType(
                        String(first.reward_type || "").replace(/_/g, " ") || ""
                    );
                    setVisits(first.cost_visits || 6);
                } else if (data?.loyalty) {
                    // Legacy loyalty object — no reward_id to PATCH, treat as no reward
                    setRewardId(null);
                    setRewardType(data.loyalty.reward_type || "");
                    setVisits(data.loyalty.visits || 6);
                } else {
                    setRewardId(null);
                    setRewardType("");
                    setVisits(6);
                }
            } catch (err) {
                if (!cancelled) {
                    showToast?.(
                        err?.response?.data?.error || "Could not load loyalty settings.",
                        "error"
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadSettings();
        return () => {
            cancelled = true;
        };
    }, [showToast, userInfo?.is_admin, userInfo?.email, adminSelectedMerchantId]);

    const handleSave = async (e) => {
        e.preventDefault();

        if (userInfo?.is_admin && !adminSelectedMerchantId) {
            showToast?.("Select a merchant first.", "warning");
            return;
        }

        if (!rewardId) {
            showToast?.("No existing reward to edit yet.", "warning");
            return;
        }

        setSaving(true);
        try {
            await backendAPI.patch(
                `/api/dashboard/merchant/rewards/${rewardId}/`,
                {
                    reward_type: rewardType,
                    cost_visits: Math.max(1, Number(visits) || 1),
                },
                { params: adminMerchantQuery() }
            );
            const label = String(rewardType || "").trim();
            const visitCount = Math.max(1, Number(visits) || 1);
            showToast?.(
                label
                    ? `Reward updated: ${label} (${visitCount} visit${visitCount === 1 ? "" : "s"}).`
                    : "Reward updated successfully.",
                "success"
            );
        } catch (err) {
            showToast?.(
                err?.response?.data?.error || "Could not save loyalty settings.",
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="klerk-app-container">
            <div className="klerk-sidebar">
                <div className="klerk-logo">Klerk</div>
                <MerchantSidebarMenu dashboardEnd />
            </div>

            <div className="klerk-main-content">
                <div className="klerk-header d-flex justify-content-between align-items-center mb-4">
                    <h4 className="page-title m-0">{t('sidebar.loyaltySettings')}</h4>

                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                        <div className="user-profile-widget d-flex align-items-center">
                            <span className="store-name">{businessName}{" "}</span>
                            <span className="status-badge">{t('profile.active')}</span>
                            <ProfileDropdown />
                        </div>
                        <button
                            type="button"
                            className="klerk-btn btn-secondary klerk-header-logout"
                            onClick={logoutHandler}
                        >
                            {t('profile.logOut')}
                        </button>
                    </div>
                </div>

                {userInfo?.is_admin ? (
                    <div className="klerk-card mb-4 m-0">
                        <h6 className="card-title-sm">{t('loyaltySettings.merchantAdmin')}</h6>
                        <select
                            className="form-select"
                            style={{ maxWidth: "420px", borderRadius: "10px", border: "1px solid #e2e8f0" }}
                            value={adminSelectedMerchantId}
                            onChange={(e) => setAdminSelectedMerchantId(e.target.value)}
                            aria-label="Select merchant for loyalty settings"
                        >
                            <option value="">{t('loyaltySettings.selectMerchant')}</option>
                            {adminMerchants.map((m) => (
                                <option key={m.id || m.merchant_id} value={m.merchant_id || m.id}>
                                    {m.businessName || m.business_name || m.merchant_id}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}

                <div className="klerk-card program-card">
                    {loading ? (
                        <p>{t('loyaltySettings.loading')}</p>
                    ) : !rewardId ? (
                        <p className="text-muted small mb-0">
                            No reward set up yet. Add one from the{" "}
                            <a href="/homepage" className="text-decoration-underline">Dashboard</a> to edit it here.
                        </p>
                    ) : (
                        <form className="reward-program-form" onSubmit={handleSave}>
                            <div className="mb-3">
                                <label
                                    className="form-label small mb-1"
                                    htmlFor="reward-type"
                                >
                                    {t('loyaltySettings.rewardType')}
                                </label>
                                <input
                                    id="reward-type"
                                    type="text"
                                    className="form-control"
                                    value={rewardType}
                                    onChange={(e) => setRewardType(e.target.value)}
                                    placeholder={t('loyaltySettings.rewardTypePlaceholder')}
                                />
                            </div>

                            <div className="mb-3">
                                <label
                                    className="form-label small mb-1"
                                    htmlFor="visits-required"
                                >
                                    {t('loyaltySettings.visitsRequired')}
                                </label>
                                <input
                                    id="visits-required"
                                    type="number"
                                    min={1}
                                    className="form-control"
                                    value={visits}
                                    onChange={(e) => setVisits(e.target.value)}
                                />
                            </div>

                            <button
                                type="submit"
                                className="klerk-btn btn-primary"
                                disabled={saving}
                            >
                                {saving ? t('home.saving') : t('home.save')}
                            </button>
                        </form>
                    )}
                </div>

                <div className="mt-4">
                    <PostComposer
                        merchantId={userInfo?.is_admin && adminSelectedMerchantId ? adminSelectedMerchantId : undefined}
                        merchantName={userInfo?.is_admin && adminSelectedMerchantId
                            ? (adminMerchants.find((m) => (m.merchant_id || m.id) === adminSelectedMerchantId)?.businessName ||
                               adminMerchants.find((m) => (m.merchant_id || m.id) === adminSelectedMerchantId)?.business_name)
                            : undefined}
                    />
                </div>
            </div>
        </div>
    );
};

export default LoyaltySettingsPage;
